import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { CATEGORIES, API_CATEGORIES, CATEGORY_LABEL } from "../lib/catalog";
import { LogOut, RefreshCw, Upload, Trash2, Pencil, Plus, X } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Quoted", "Converted", "Closed"];
const STATUS_COLOR = {
  New: "bg-primary/15 text-secondary border-primary/30",
  Contacted: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
  Quoted: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  Converted: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  Closed: "bg-muted text-muted-foreground border-black/10",
};

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("leads");

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.24em] text-secondary font-bold">Admin</div>
          <h1 className="font-display text-2xl md:text-3xl font-black">Dashboard</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-muted-foreground hidden sm:block">{user?.email}</div>
          <Button data-testid="admin-logout" variant="outline" className="rounded-full border-black/15" onClick={logout}>
            <LogOut className="h-4 w-4 mr-1.5" /> Logout
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-card border border-black/10 rounded-full p-1">
          <TabsTrigger data-testid="tab-leads" value="leads" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-black">Leads</TabsTrigger>
          <TabsTrigger data-testid="tab-models" value="models" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-black">Battery Models</TabsTrigger>
          <TabsTrigger data-testid="tab-settings" value="settings" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-black">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="leads" className="mt-6"><LeadsPanel /></TabsContent>
        <TabsContent value="models" className="mt-6"><ModelsPanel /></TabsContent>
        <TabsContent value="settings" className="mt-6"><SettingsPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function LeadsPanel() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    if (status) params.status = status;
    api.get("/admin/leads", { params })
      .then(r => setLeads(r.data || []))
      .finally(() => setLoading(false));
  }, [category, status]);

  useEffect(() => { load(); }, [load]);

  const update = async (id, s) => {
    try {
      await api.patch(`/admin/leads/${id}/status`, { status: s });
      setLeads(ls => ls.map(l => l.id === id ? { ...l, status: s } : l));
      toast.success("Status updated");
    } catch { toast.error("Failed to update"); }
  };

  return (
    <Card className="bg-card border-black/10 rounded-2xl">
      <div className="p-4 md:p-6 flex flex-col md:flex-row gap-3 md:items-center md:justify-between border-b border-black/10">
        <div className="flex gap-3 flex-wrap">
          <Select value={category} onValueChange={(v) => setCategory(v === "__all" ? "" : v)}>
            <SelectTrigger data-testid="filter-category" className="w-56 bg-muted/40 border-black/10"><SelectValue placeholder="All Categories" /></SelectTrigger>
            <SelectContent className="bg-card border-black/10">
              <SelectItem value="__all">All Categories</SelectItem>
              {API_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={(v) => setStatus(v === "__all" ? "" : v)}>
            <SelectTrigger data-testid="filter-status" className="w-48 bg-muted/40 border-black/10"><SelectValue placeholder="All Statuses" /></SelectTrigger>
            <SelectContent className="bg-card border-black/10">
              <SelectItem value="__all">All Statuses</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-muted-foreground">{leads.length} leads</div>
          <Button variant="outline" size="sm" className="rounded-full border-black/15" onClick={load} data-testid="refresh-leads"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-black/10 hover:bg-transparent">
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Model</TableHead>
              <TableHead>City</TableHead>
              <TableHead>Received</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Loading…</TableCell></TableRow>
            ) : leads.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No leads yet.</TableCell></TableRow>
            ) : leads.map(l => (
              <TableRow key={l.id} data-testid={`lead-row-${l.id}`} className="border-black/10 hover:bg-black/[0.04]">
                <TableCell className="font-medium">
                  <div>{l.full_name}</div>
                  <div className="text-xs text-muted-foreground">{l.email || "—"}</div>
                </TableCell>
                <TableCell><a href={`tel:${l.phone}`} className="hover:text-secondary">{l.phone}</a></TableCell>
                <TableCell>{CATEGORY_LABEL[l.product_category] || l.product_category}</TableCell>
                <TableCell className="text-sm">{l.selected_model || "—"}</TableCell>
                <TableCell className="text-sm">{l.city_area || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString()}</TableCell>
                <TableCell>
                  <Select value={l.status} onValueChange={(v) => update(l.id, v)}>
                    <SelectTrigger data-testid={`status-select-${l.id}`} className={`w-36 border ${STATUS_COLOR[l.status] || ""}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-black/10">
                      {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

const emptyModel = { category: "automotive", brand_series: "", model_code: "", capacity: "", application: "", warranty: "", price_range: "", active: true };

function ModelsPanel() {
  const [models, setModels] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/battery-models").then(r => setModels(r.data || []));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this battery model?")) return;
    try { await api.delete(`/admin/battery-models/${id}`); await load(); toast.success("Deleted"); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <Card className="bg-card border-black/10 rounded-2xl">
      <div className="p-4 md:p-6 flex items-center justify-between border-b border-black/10">
        <div>
          <div className="font-display font-bold text-lg">Battery Models</div>
          <div className="text-sm text-muted-foreground">Add, edit or remove recommended models.</div>
        </div>
        <Button data-testid="btn-add-model" onClick={() => setEditing({ ...emptyModel, __new: true })} className="rounded-full bg-primary text-black hover:bg-secondary hover:text-white"><Plus className="h-4 w-4 mr-1.5" /> Add Model</Button>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-black/10">
              <TableHead>Category</TableHead>
              <TableHead>Series</TableHead>
              <TableHead>Model Code</TableHead>
              <TableHead>Capacity</TableHead>
              <TableHead>Warranty</TableHead>
              <TableHead>Application</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {models.map(m => (
              <TableRow key={m.id} className="border-black/10 hover:bg-black/[0.04]">
                <TableCell><Badge variant="outline" className="border-black/10">{CATEGORY_LABEL[m.category]}</Badge></TableCell>
                <TableCell className="font-semibold">{m.brand_series}</TableCell>
                <TableCell className="text-secondary">{m.model_code}</TableCell>
                <TableCell>{m.capacity}</TableCell>
                <TableCell>{m.warranty}</TableCell>
                <TableCell className="text-sm">{m.application}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="rounded-full border-black/15 h-8" onClick={() => setEditing({ ...m })} data-testid={`edit-model-${m.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="outline" className="rounded-full border-red-500/30 text-red-400 hover:bg-red-500/10 h-8" onClick={() => remove(m.id)} data-testid={`delete-model-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {editing && (
        <ModelEditor
          value={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </Card>
  );
}

function ModelEditor({ value, onClose, onSaved }) {
  const [m, setM] = useState(value);
  const [saving, setSaving] = useState(false);
  const isNew = !!m.__new;
  const set = (k, v) => setM(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        category: m.category, brand_series: m.brand_series, model_code: m.model_code,
        capacity: m.capacity, application: m.application, warranty: m.warranty,
        price_range: m.price_range, filters: m.filters || {}, active: m.active !== false,
      };
      if (isNew) await api.post("/admin/battery-models", payload);
      else await api.put(`/admin/battery-models/${m.id}`, payload);
      toast.success("Saved");
      onSaved();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <Card className="bg-card border-black/10 rounded-2xl p-6 max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-bold text-xl">{isNew ? "Add Model" : "Edit Model"}</div>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Category">
            <Select value={m.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="model-form-category" className="bg-muted/40 border-black/10 h-11"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-black/10">
                {API_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Brand / Series"><Input data-testid="model-form-brand" value={m.brand_series} onChange={e => set("brand_series", e.target.value)} className="bg-muted/40 border-black/10 h-11" /></Field>
          <Field label="Model Code"><Input data-testid="model-form-code" value={m.model_code} onChange={e => set("model_code", e.target.value)} className="bg-muted/40 border-black/10 h-11" /></Field>
          <Field label="Capacity"><Input value={m.capacity} onChange={e => set("capacity", e.target.value)} className="bg-muted/40 border-black/10 h-11" placeholder="e.g. 150 Ah" /></Field>
          <Field label="Warranty"><Input value={m.warranty} onChange={e => set("warranty", e.target.value)} className="bg-muted/40 border-black/10 h-11" placeholder="e.g. 48 Months" /></Field>
          <Field label="Price Range"><Input value={m.price_range || ""} onChange={e => set("price_range", e.target.value)} className="bg-muted/40 border-black/10 h-11" placeholder="e.g. ₹6,800 - ₹7,400" /></Field>
          <div className="md:col-span-2"><Field label="Application"><Textarea value={m.application} onChange={e => set("application", e.target.value)} className="bg-muted/40 border-black/10 min-h-[70px]" /></Field></div>
        </div>
        <div className="mt-5 flex justify-end gap-3">
          <Button variant="outline" className="rounded-full border-black/15" onClick={onClose}>Cancel</Button>
          <Button data-testid="model-form-save" disabled={saving} onClick={save} className="rounded-full bg-primary text-black hover:bg-secondary hover:text-white">{saving ? "Saving…" : "Save Model"}</Button>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function SettingsPanel() {
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  useEffect(() => { api.get("/logo").then(r => setLogo(r.data?.logo)).catch(() => {}); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1_000_000) { toast.error("Max 1MB"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogo(data.logo);
      toast.success("Logo updated");
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <Card className="bg-card border-black/10 rounded-2xl p-6">
      <div className="font-display font-bold text-xl">Business Logo</div>
      <p className="text-sm text-muted-foreground mt-1">Upload your business logo (PNG/JPG/WEBP/SVG · max 1MB). Displays across site header and footer.</p>
      <div className="mt-6 flex items-center gap-6 flex-wrap">
        <div className="h-24 w-24 rounded-2xl border border-black/10 bg-muted/40 flex items-center justify-center overflow-hidden">
          {logo ? <img src={logo} alt="Logo" className="h-full w-full object-contain" /> : <span className="text-xs text-muted-foreground">No logo</span>}
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="logo-file-input" />
          <Button data-testid="upload-logo-btn" disabled={busy} onClick={() => fileRef.current?.click()} className="rounded-full bg-primary text-black hover:bg-secondary hover:text-white">
            <Upload className="h-4 w-4 mr-1.5" /> {busy ? "Uploading…" : (logo ? "Replace Logo" : "Upload Logo")}
          </Button>
          <p className="mt-2 text-xs text-muted-foreground">Recommended: square PNG with transparent background.</p>
        </div>
      </div>
    </Card>
  );
}
